/**
 * Formulario de ingreso del cotizador (réplica del `<form id="cotizadorForm">` del SSR).
 *
 * Estado de UI local (selecciones del formulario) en `useState`; valida con Zod
 * (`cotizadorFormSchema`) antes de delegar el cálculo al padre (que llama a la API). No hace
 * `fetch` ni toca el motor: solo arma el payload `CotizadorInput` y lo entrega.
 *
 * Reproduce la lógica de campos condicionales del SSR (`toggleModeloFields` /
 * `toggleMedidasTipo`):
 *  - Venas: modelos 3/4/7.
 *  - Ancho de vidrio / Espacio por vena: modelos 7 / 4 (con etiqueta y default distintos).
 *  - Sub-modelo de Dilatación con vidrio: modelo 7.
 *  - Estilo Verona: modelo 3.
 *  - Medidas iguales (ancho/alto) vs diferentes (una fila por puerta).
 *  - Modo de cobro: solo admin lo elige; no-admin va fijo a `m2`.
 */
import { useMemo, useState } from "react";

import { Button, Input } from "@/components/ui";

import {
  CALIBRES,
  CALIBRES_MARCO,
  INITIAL_FORM,
  MODELO_DILATACION,
  MODELO_DILATACION_VIDRIO,
  MODELO_VERONA,
  MODELOS,
  MODELOS_CON_VENAS,
  MODOS_COBRO,
  SUB_MODELOS_DV,
  VERONA_ESTILOS,
} from "../constants";
import { cotizadorFormSchema } from "../schemas";
import type { CotizadorInput, DoorMeasure } from "../types";

interface CotizadorFormProps {
  isAdmin: boolean;
  /** `true` mientras el cálculo está en vuelo (deshabilita el botón). */
  calculating: boolean;
  /** Entrega el payload validado para que el padre lo calcule contra la API. */
  onSubmit: (input: CotizadorInput) => void;
}

/** Errores por campo (de Zod); las medidas por puerta se indexan por su posición. */
interface FieldErrors {
  ancho?: string;
  alto?: string;
  medidas?: string;
  medidasRows?: Record<number, string>;
}

const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600";
const inputClass = "text-sm";
const groupClass = "flex flex-col";

export function CotizadorForm({ isAdmin, calculating, onSubmit }: CotizadorFormProps) {
  // Estado del formulario. `ancho`/`alto` como string para permitir el campo vacío inicial
  // (el SSR los arrancaba vacíos y validaba al enviar).
  const [modeloId, setModeloId] = useState(INITIAL_FORM.modelo_id);
  const [calibre, setCalibre] = useState(INITIAL_FORM.calibre);
  const [cantidad, setCantidad] = useState(INITIAL_FORM.cantidad);
  const [chapaBola, setChapaBola] = useState(INITIAL_FORM.chapa_bola);
  const [venas, setVenas] = useState(INITIAL_FORM.venas);
  const [anchoVena, setAnchoVena] = useState(INITIAL_FORM.ancho_vena);
  const [subModeloDv, setSubModeloDv] = useState(INITIAL_FORM.sub_modelo_dv);
  const [calibreMarco, setCalibreMarco] = useState(INITIAL_FORM.calibre_marco);
  const [veronaEstilo, setVeronaEstilo] = useState(INITIAL_FORM.verona_estilo);
  const [modoCobro, setModoCobro] = useState(INITIAL_FORM.modo_cobro);
  const [medidasDiferentes, setMedidasDiferentes] = useState(false);
  const [ancho, setAncho] = useState("");
  const [alto, setAlto] = useState("");
  const [medidas, setMedidas] = useState<{ ancho: string; alto: string }[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});

  const showVenas = MODELOS_CON_VENAS.has(modeloId);
  const showVidrio = modeloId === MODELO_DILATACION_VIDRIO || modeloId === MODELO_DILATACION;
  const showSubModelo = modeloId === MODELO_DILATACION_VIDRIO;
  const showVerona = modeloId === MODELO_VERONA;
  const vidrioLabel =
    modeloId === MODELO_DILATACION_VIDRIO ? "Ancho vidrio (cm)" : "Espacio por vena (cm)";

  // Filas de medidas por puerta, ajustadas a la cantidad (réplica de
  // `generarTablaMedidasPorPuerta`): tantas filas como `cantidad`, conservando lo ya tecleado.
  const measureRows = useMemo(() => {
    const rows: { ancho: string; alto: string }[] = [];
    for (let i = 0; i < Math.max(1, cantidad); i += 1) {
      rows.push(medidas[i] ?? { ancho: "", alto: "" });
    }
    return rows;
  }, [cantidad, medidas]);

  function setMeasureAt(index: number, patch: Partial<{ ancho: string; alto: string }>) {
    setMedidas(() => {
      // Parte de las filas vigentes (ya dimensionadas a `cantidad`) y aplica el cambio.
      const next = measureRows.map((row) => ({ ...row }));
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    const parsedAncho = ancho === "" ? undefined : Number(ancho);
    const parsedAlto = alto === "" ? undefined : Number(alto);
    const parsedMedidas: DoorMeasure[] = measureRows.map((m) => ({
      ancho: m.ancho === "" ? Number.NaN : Number(m.ancho),
      alto: m.alto === "" ? Number.NaN : Number(m.alto),
    }));

    const candidate = {
      modelo_id: modeloId,
      calibre,
      cantidad,
      chapa_bola: chapaBola,
      venas,
      ancho_vena: anchoVena,
      sub_modelo_dv: subModeloDv,
      calibre_marco: calibreMarco,
      verona_estilo: veronaEstilo,
      modo_cobro: isAdmin ? modoCobro : "m2",
      medidas_diferentes: medidasDiferentes,
      ancho: parsedAncho,
      alto: parsedAlto,
      medidas: medidasDiferentes ? parsedMedidas : undefined,
    };

    const result = cotizadorFormSchema.safeParse(candidate);
    if (!result.success) {
      const next: FieldErrors = {};
      const rowErrors: Record<number, string> = {};
      for (const issue of result.error.issues) {
        const [head, idx] = issue.path;
        if (head === "ancho") next.ancho = issue.message;
        else if (head === "alto") next.alto = issue.message;
        else if (head === "medidas" && typeof idx === "number") rowErrors[idx] = issue.message;
        else if (head === "medidas") next.medidas = issue.message;
      }
      if (Object.keys(rowErrors).length > 0) next.medidasRows = rowErrors;
      setErrors(next);
      return;
    }

    setErrors({});
    // Construye el payload final (sin las claves que no aplican al modo).
    const payload: CotizadorInput = {
      modelo_id: result.data.modelo_id,
      calibre: result.data.calibre,
      cantidad: result.data.cantidad,
      chapa_bola: result.data.chapa_bola,
      venas: result.data.venas,
      ancho_vena: result.data.ancho_vena,
      sub_modelo_dv: result.data.sub_modelo_dv,
      calibre_marco: result.data.calibre_marco,
      verona_estilo: result.data.verona_estilo,
      modo_cobro: result.data.modo_cobro,
      medidas_diferentes: result.data.medidas_diferentes,
    };
    if (result.data.medidas_diferentes) {
      // El esquema garantiza `medidas` completas en este modo (refine de longitud).
      const validMedidas = result.data.medidas ?? [];
      payload.medidas = validMedidas;
      // Compatibilidad con el backend (primera medida como base), igual que el SSR.
      const first = validMedidas[0];
      if (first) {
        payload.ancho = first.ancho;
        payload.alto = first.alto;
      }
    } else if (result.data.ancho !== undefined && result.data.alto !== undefined) {
      // El esquema garantiza ancho/alto positivos en modo "iguales".
      payload.ancho = result.data.ancho;
      payload.alto = result.data.alto;
    }
    onSubmit(payload);
  }

  return (
    <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={handleSubmit} noValidate>
      <div className={`${groupClass} sm:col-span-2`}>
        <label className={labelClass} htmlFor="cot-modelo">
          Modelo
        </label>
        <select
          id="cot-modelo"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={modeloId}
          onChange={(e) => setModeloId(e.target.value)}
        >
          {MODELOS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <div className={groupClass}>
        <label className={labelClass} htmlFor="cot-calibre">
          Calibre
        </label>
        <select
          id="cot-calibre"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={calibre}
          onChange={(e) => setCalibre(e.target.value)}
        >
          {CALIBRES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className={groupClass}>
        <label className={labelClass} htmlFor="cot-cantidad">
          Cantidad
        </label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Reducir cantidad"
            onClick={() => setCantidad((v) => Math.max(1, v - 1))}
          >
            −
          </Button>
          <Input
            id="cot-cantidad"
            type="number"
            min={1}
            className={inputClass}
            value={cantidad}
            onChange={(e) => setCantidad(Math.max(1, Number(e.target.value) || 1))}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Aumentar cantidad"
            onClick={() => setCantidad((v) => v + 1)}
          >
            +
          </Button>
        </div>
      </div>

      <div className={`${groupClass} sm:col-span-2`}>
        <span className={labelClass}>Ancho / Alto</span>
        <div className="flex flex-wrap gap-4 text-sm text-slate-700">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="medidas_tipo"
              checked={!medidasDiferentes}
              onChange={() => setMedidasDiferentes(false)}
            />
            Medidas iguales
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="medidas_tipo"
              checked={medidasDiferentes}
              onChange={() => setMedidasDiferentes(true)}
            />
            Medidas diferentes
          </label>
        </div>
      </div>

      {!medidasDiferentes ? (
        <>
          <div className={groupClass}>
            <label className={labelClass} htmlFor="cot-ancho">
              Ancho (cm)
            </label>
            <Input
              id="cot-ancho"
              type="number"
              step="0.1"
              min={1}
              placeholder="Ej: 80.0"
              className={inputClass}
              value={ancho}
              aria-invalid={errors.ancho ? true : undefined}
              aria-describedby={errors.ancho ? "cot-ancho-error" : undefined}
              onChange={(e) => setAncho(e.target.value)}
            />
            {errors.ancho ? (
              <p id="cot-ancho-error" className="mt-1 text-sm text-red-600">
                {errors.ancho}
              </p>
            ) : null}
          </div>
          <div className={groupClass}>
            <label className={labelClass} htmlFor="cot-alto">
              Alto (cm)
            </label>
            <Input
              id="cot-alto"
              type="number"
              step="0.1"
              min={1}
              placeholder="Ej: 200.0"
              className={inputClass}
              value={alto}
              aria-invalid={errors.alto ? true : undefined}
              aria-describedby={errors.alto ? "cot-alto-error" : undefined}
              onChange={(e) => setAlto(e.target.value)}
            />
            {errors.alto ? (
              <p id="cot-alto-error" className="mt-1 text-sm text-red-600">
                {errors.alto}
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <div className={`${groupClass} sm:col-span-2`}>
          <span className={labelClass}>Medidas por puerta</span>
          <div className="flex max-h-64 flex-col gap-2 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2">
            {measureRows.map((row, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="w-8 text-center text-sm font-semibold text-slate-600">
                  {idx + 1}
                </span>
                <Input
                  type="number"
                  step="0.1"
                  min={1}
                  placeholder="Ancho"
                  aria-label={`Ancho puerta ${idx + 1}`}
                  className={inputClass}
                  value={row.ancho}
                  onChange={(e) => setMeasureAt(idx, { ancho: e.target.value })}
                />
                <Input
                  type="number"
                  step="0.1"
                  min={1}
                  placeholder="Alto"
                  aria-label={`Alto puerta ${idx + 1}`}
                  className={inputClass}
                  value={row.alto}
                  onChange={(e) => setMeasureAt(idx, { alto: e.target.value })}
                />
              </div>
            ))}
          </div>
          {errors.medidas ? (
            <p className="mt-1 text-sm text-red-600">{errors.medidas}</p>
          ) : null}
          {errors.medidasRows
            ? Object.entries(errors.medidasRows).map(([idx, msg]) => (
                <p key={idx} className="mt-1 text-sm text-red-600">
                  {msg}
                </p>
              ))
            : null}
        </div>
      )}

      <div className={groupClass}>
        <span className={labelClass}>Opciones</span>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={chapaBola}
            onChange={(e) => setChapaBola(e.target.checked)}
          />
          Chapa bola (11 cm)
        </label>
      </div>

      <div className={groupClass}>
        <span className={labelClass}>Modo cobro</span>
        {isAdmin ? (
          <div className="flex flex-wrap gap-3 text-sm text-slate-700" role="radiogroup" aria-label="Modo de cobro">
            {MODOS_COBRO.map((m) => (
              <label key={m.value} className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="modo_cobro"
                  value={m.value}
                  checked={modoCobro === m.value}
                  onChange={() => setModoCobro(m.value)}
                />
                {m.label}
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Cobro por M²</p>
        )}
      </div>

      <div className={`${groupClass} sm:col-span-2`}>
        <span className={labelClass}>Calibre marco</span>
        <div className="flex flex-wrap gap-3 text-sm text-slate-700" role="radiogroup" aria-label="Calibre marco">
          {CALIBRES_MARCO.map((c) => (
            <label key={c.value} className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="calibre_marco"
                value={c.value}
                checked={calibreMarco === c.value}
                onChange={() => setCalibreMarco(c.value)}
              />
              {c.label}
            </label>
          ))}
        </div>
      </div>

      {showVenas ? (
        <div className={groupClass}>
          <label className={labelClass} htmlFor="cot-venas">
            Número de venas
          </label>
          <Input
            id="cot-venas"
            type="number"
            min={1}
            className={inputClass}
            value={venas}
            onChange={(e) => setVenas(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      ) : null}

      {showVidrio ? (
        <div className={groupClass}>
          <label className={labelClass} htmlFor="cot-ancho-vena">
            {vidrioLabel}
          </label>
          <Input
            id="cot-ancho-vena"
            type="number"
            step="0.1"
            min={0.1}
            className={inputClass}
            value={anchoVena}
            onChange={(e) => setAnchoVena(Number(e.target.value) || 0)}
          />
        </div>
      ) : null}

      {showSubModelo ? (
        <div className={`${groupClass} sm:col-span-2`}>
          <label className={labelClass} htmlFor="cot-sub-modelo">
            Sub-modelo (Dilatación con vidrio)
          </label>
          <select
            id="cot-sub-modelo"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={subModeloDv}
            onChange={(e) => setSubModeloDv(Number(e.target.value))}
          >
            {SUB_MODELOS_DV.map((s) => (
              <option key={s.value} value={s.value}>
                {s.code} - {s.name} ({s.desc})
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {showVerona ? (
        <div className={groupClass}>
          <label className={labelClass} htmlFor="cot-verona">
            Estilo Verona
          </label>
          <select
            id="cot-verona"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={veronaEstilo}
            onChange={(e) => setVeronaEstilo(e.target.value)}
          >
            {VERONA_ESTILOS.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="sm:col-span-2">
        <Button type="submit" variant="primary" className="w-full" disabled={calculating}>
          {calculating ? "Cotizando…" : "Cotizar"}
        </Button>
      </div>
    </form>
  );
}
